import Big from 'big.js';
import {combineLatest} from 'rxjs';
import {filter, map, shareReplay, throttleTime} from 'rxjs/operators';
import {numUtil} from '@/helper/num';
import {spotsCache$} from '@/config';
import {calcPriceWU$} from '@/calc/fiat';
import {spotMarket24hData$} from '@/raw/market24h-spot';
import {indexPricesCache$} from '@/price-index/price-index';
import type {ws, CalcPriceWU, SpotMarket, SpotPair, PriceEntity} from '@/types';
type SpotMarketData = ws.SpotMarketData;

export const spotMarket24h$ = combineLatest([spotsCache$, spotMarket24hData$, calcPriceWU$, indexPricesCache$]).pipe(
  throttleTime(50),
  map(convertSpotMarkets),
  filter((list: SpotMarket[]) => list.length > 0),
  shareReplay(1)
);

function convertSpotMarkets([spotsCache, spotMarketData, calcPriceWU, indexPricesCache]: [
  Map<string, SpotPair>,
  SpotMarketData[],
  CalcPriceWU,
  Map<string, PriceEntity>
]): SpotMarket[] {
  return spotMarketData
    .map((o: SpotMarketData) => convertSpotMarket(spotsCache, o, calcPriceWU, indexPricesCache))
    .filter(Boolean);
}

function convertSpotMarket(
  spotsCache: Map<string, SpotPair>,
  spotMarketData: SpotMarketData,
  calcPriceWU: CalcPriceWU,
  indexPricesCache: Map<string, PriceEntity>
): SpotMarket {
  const {symbol} = spotMarketData;
  const spot = spotsCache.get(symbol);
  if (!spot) {
    return null;
  }

  const {
    bidEp,
    askEp,
    lastEp: closeEp,
    highEp,
    lowEp,
    openEp,
    indexEp,
    turnoverEv: turnoverEv2,
    volumeEv,
  } = spotMarketData;
  const {
    type,
    displaySymbol,
    name,
    baseCurrency,
    quoteCurrency,
    displayBaseCurrency,
    pricePrecision,
    quoteQtyScale,
    quoteQtyPrecision,
    status,
    baseQtyPrecision,
    baseQtyScale,
    urlSymbol,
  } = spot;
  const priceScale = quoteQtyScale;
  const turnoverEv = genTurnoverEvWithTry(indexPricesCache, quoteCurrency, turnoverEv2);
  const turnover = numUtil.floor(turnoverEv, quoteQtyPrecision, quoteQtyScale);
  const volume = numUtil.floor(volumeEv, baseQtyPrecision, baseQtyScale);
  const changePercent = genChangePercent(closeEp, openEp);
  const change24hEp = closeEp - openEp;
  const change24h = numUtil.delimit(numUtil.floor(change24hEp, pricePrecision, priceScale));
  if (status !== 'Listed') {
    return createInit(spot);
  }
  return {
    type,
    symbol,
    status,
    displaySymbol,
    baseCurrency,
    bid1Ep: bidEp,
    ask1Ep: askEp,
    openEp,
    open: numUtil.floor(openEp, pricePrecision, priceScale),
    closeEp,
    close: numUtil.delimit(numUtil.floor(closeEp, pricePrecision, priceScale)),
    closeMoneyWU: calcPriceWU(closeEp, priceScale, quoteCurrency),
    highEp,
    high: numUtil.delimit(numUtil.floor(highEp, pricePrecision, priceScale)),
    lowEp,
    low: numUtil.delimit(numUtil.floor(lowEp, pricePrecision, priceScale)),
    indexPriceEp: indexEp,
    indexPrice: numUtil.floor(indexEp, pricePrecision, priceScale),
    turnoverEv,
    turnoverStr: numUtil.abbreviateNumber(turnover),
    volume24hStr: numUtil.abbreviateNumber(volume),
    changePercent,
    changePercentStr: genChangePercentStr(changePercent),
    change24hEp,
    change24hStr: gen24hChangeStr(change24hEp, change24h),
    name,
    displayBaseCurrency,
    klineData: [],
    quoteCurrency,
    combinationTurnoverEv: Number(turnover),
    volumeEv,
    urlSymbol,
  };
}

function gen24hChangeStr(change24hEp, change24h): string {
  const prefix = change24hEp > 0 ? '+' : '';
  return prefix + change24h;
}

function genChangePercent(closeEp, openEp): number {
  if (closeEp === 0 || openEp === 0 || closeEp === openEp) {
    return 0;
  }
  const changeEp = closeEp - openEp;
  return (changeEp * 100) / openEp;
}

function genChangePercentStr(changePercent): string {
  if (changePercent === 0) {
    return '0.00%';
  }

  const prefix = changePercent > 0 ? '+' : '';
  const percent = numUtil.floor(changePercent, 2);
  return prefix + percent + '%';
}

function createInit({type, symbol, status, displaySymbol, baseCurrency, name}): SpotMarket {
  return {
    type,
    symbol,
    status,
    displaySymbol,
    baseCurrency,
    bid1Ep: 0,
    ask1Ep: 0,
    openEp: 0,
    open: '--',
    closeEp: 0,
    close: '--',
    closeMoneyWU: '--',
    highEp: 0,
    high: '--',
    lowEp: 0,
    low: '--',
    indexPriceEp: 0,
    indexPrice: '--',
    turnoverEv: 0,
    turnoverStr: '--',
    volume24hStr: '--',
    changePercent: 0,
    changePercentStr: '--',
    change24hEp: 0,
    change24hStr: '--',
    name,
    displayBaseCurrency: '',
    klineData: [],
    quoteCurrency: 'USDT',
    combinationTurnoverEv: 0,
    volumeEv: 0,
    urlSymbol: '',
  };
}

function genTurnoverEvWithTry(indexPricesCache, quoteCurrency, turnoverEv) {
  if (quoteCurrency === 'TRY') {
    const rate = indexPricesCache.get('.' + quoteCurrency);
    return rate ? Big(turnoverEv).times(rate.priceEp).div(rate.priceFactor).toNumber() : 0;
  }
  return turnoverEv;
}
